import { API_BASE_URL } from "../config/api";
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";

export default function ProductMapping() {
  const location = useLocation();
  const isAdminView = location.pathname.startsWith("/admin");

  // viewMode: "unmapped" | "mapped" | "ignored"
  const [viewMode, setViewMode] = useState("unmapped");
  const [itemType, setItemType] = useState("purchase"); // "purchase" | "sale"

  const [purchaseItems, setPurchaseItems] = useState([]);
  const [salesItems, setSalesItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchaseSearchQuery, setPurchaseSearchQuery] = useState("");
  const [salesSearchQuery, setSalesSearchQuery] = useState("");

  // Product Master search states per item
  const [productSearchQueries, setProductSearchQueries] = useState({});
  const [selectedProductIds, setSelectedProductIds] = useState({});
  const [searchDropdownOpen, setSearchDropdownOpen] = useState({});
  const [allProducts, setAllProducts] = useState([]);

  // Modal for creating a new product
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [currentItemToMap, setCurrentItemToMap] = useState(null);
  const [newProductData, setNewProductData] = useState({
    productName: "",
    model: "",
    type: "",
    voltage: "",
    pole: "",
    brand: "",
    description: "",
    hsnCode: "85362000",
    listPrice: "",
    unit: "No",
    itemCode: ""
  });

  const [actionSaving, setActionSaving] = useState({});

  // Load invoice items
  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      const [purchaseRes, salesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/account-documents/mapping-items`, {
          params: {
            entryType: "purchase",
            isMapped: viewMode === "mapped" ? "true" : "false",
            isIgnored: viewMode === "ignored" ? "true" : "false"
          }
        }),
        axios.get(`${API_BASE_URL}/api/account-documents/mapping-items`, {
          params: {
            entryType: "sale",
            isMapped: viewMode === "mapped" ? "true" : "false",
            isIgnored: viewMode === "ignored" ? "true" : "false"
          }
        })
      ]);
      setPurchaseItems(Array.isArray(purchaseRes.data) ? purchaseRes.data : []);
      setSalesItems(Array.isArray(salesRes.data) ? salesRes.data : []);
    } catch (error) {
      console.error("Failed to fetch mapping items:", error);
      setPurchaseItems([]);
      setSalesItems([]);
    } finally {
      setLoading(false);
    }
  }, [viewMode]);

  const loadAllProducts = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/material-stock/inventory`);
      setAllProducts(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error("Failed to fetch all products:", error);
    }
  }, []);

  useEffect(() => {
    loadItems();
    loadAllProducts();
    // Clear item-specific states
    setProductSearchQueries({});
    setSelectedProductIds({});
    setSearchDropdownOpen({});
  }, [loadItems, loadAllProducts]);

  // Search material stock catalog locally
  const searchProductMaster = (itemId, query) => {
    setProductSearchQueries(prev => ({ ...prev, [itemId]: query }));
  };

  // Perform mapping to existing product
  const handleMapItem = async (itemId) => {
    const productId = selectedProductIds[itemId];
    if (!productId) return;

    setActionSaving(prev => ({ ...prev, [itemId]: true }));
    try {
      await axios.post(`${API_BASE_URL}/api/account-documents/map-item`, {
        documentId: itemId,
        productId
      });
      loadItems();
      loadAllProducts();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to map item");
    } finally {
      setActionSaving(prev => ({ ...prev, [itemId]: false }));
    }
  };

  // Perform unmapping
  const handleUnmapItem = async (itemId) => {
    if (!window.confirm("Are you sure you want to unmap this item? This will reverse the stock transaction.")) {
      return;
    }

    setActionSaving(prev => ({ ...prev, [itemId]: true }));
    try {
      await axios.post(`${API_BASE_URL}/api/account-documents/unmap-item`, {
        documentId: itemId
      });
      loadItems();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to unmap item");
    } finally {
      setActionSaving(prev => ({ ...prev, [itemId]: false }));
    }
  };

  // Ignore item in mapping list
  const handleIgnoreItem = async (itemId) => {
    if (!window.confirm("Are you sure you want to ignore this item? It will be moved to the Ignored Items list.")) {
      return;
    }

    setActionSaving(prev => ({ ...prev, [itemId]: true }));
    try {
      await axios.post(`${API_BASE_URL}/api/account-documents/ignore-item`, {
        documentId: itemId
      });
      loadItems();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to ignore item");
    } finally {
      setActionSaving(prev => ({ ...prev, [itemId]: false }));
    }
  };

  // Restore ignored item
  const handleUnignoreItem = async (itemId) => {
    setActionSaving(prev => ({ ...prev, [itemId]: true }));
    try {
      await axios.post(`${API_BASE_URL}/api/account-documents/unignore-item`, {
        documentId: itemId
      });
      loadItems();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to restore item");
    } finally {
      setActionSaving(prev => ({ ...prev, [itemId]: false }));
    }
  };

  // Open "Create & Map" modal
  const openCreateModal = (item) => {
    setCurrentItemToMap(item);
    setNewProductData({
      productName: item.itemName || "",
      model: "",
      type: item.stockCategory === "consumable" ? "Consumable" : "Spare",
      voltage: "",
      pole: "",
      brand: "",
      description: item.description || item.itemName || "",
      hsnCode: item.hsnCode || "85362000",
      listPrice: item.unitPrice || "",
      unit: item.unit || "No",
      itemCode: ""
    });
    setShowCreateModal(true);
  };

  // Submit new product & map
  const handleCreateAndMap = async (e) => {
    e.preventDefault();
    if (!currentItemToMap) return;

    const itemId = currentItemToMap._id;
    setActionSaving(prev => ({ ...prev, [itemId]: true }));
    setShowCreateModal(false);

    try {
      await axios.post(`${API_BASE_URL}/api/account-documents/map-item`, {
        documentId: itemId,
        newProductData
      });
      loadItems();
      loadAllProducts();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to create and map item");
    } finally {
      setActionSaving(prev => ({ ...prev, [itemId]: false }));
      setCurrentItemToMap(null);
    }
  };

  // Filter items in the lists based on searchQueries and entryType
  const filteredPurchaseItems = purchaseItems
    .filter(item => item.entryType === "purchase")
    .filter(item => {
      const q = purchaseSearchQuery.toLowerCase();
      return (
        (item.documentNumber || "").toLowerCase().includes(q) ||
        (item.counterpartyName || "").toLowerCase().includes(q) ||
        (item.itemName || "").toLowerCase().includes(q) ||
        (item.itemCode || "").toLowerCase().includes(q) ||
        (item.description || "").toLowerCase().includes(q)
      );
    });

  const filteredSalesItems = salesItems
    .filter(item => item.entryType === "sale")
    .filter(item => {
      const q = salesSearchQuery.toLowerCase();
      return (
        (item.documentNumber || "").toLowerCase().includes(q) ||
        (item.counterpartyName || "").toLowerCase().includes(q) ||
        (item.itemName || "").toLowerCase().includes(q) ||
        (item.itemCode || "").toLowerCase().includes(q) ||
        (item.description || "").toLowerCase().includes(q)
      );
    });

  // Dynamic theme styling classes
  const themeAccent = isAdminView ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-amber-500 hover:bg-amber-600 text-stone-950";
  const themeCardBorder = isAdminView ? "border-blue-100" : "border-amber-100";
  const themeCardGradient = isAdminView 
    ? "bg-[linear-gradient(135deg,_#f1f5f9,_#ffffff,_#f8fafc)]" 
    : "bg-[linear-gradient(135deg,_#fffbeb,_#ffffff,_#fafaf9)]";
  const themeSecondaryActive = isAdminView ? "bg-blue-600 text-white" : "bg-amber-500 text-stone-950";

  return (
    <div className="space-y-6">
      {/* Premium Header */}
      <section className={`rounded-2xl border ${themeCardBorder} ${themeCardGradient} p-4 shadow-sm`}>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">Inventory & Invoicing</p>
        <h1 className="mt-1 text-xl font-bold text-stone-950">Product Mapping Area</h1>
        <p className="mt-1 text-xs text-stone-500">
          Map purchased and sold items to the central Product Master to reconcile Material Stock.
        </p>
      </section>

      {/* Main Controls - Switch Type & Switch Mode */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {/* Switch to Purchase vs Sold */}
        <div className="flex rounded-2xl bg-stone-100 p-1">
          <button
            type="button"
            onClick={() => setItemType("purchase")}
            className={`rounded-xl px-4 py-2 text-xs font-semibold transition ${
              itemType === "purchase" ? themeSecondaryActive : "text-stone-600 hover:text-stone-900"
            }`}
          >
            🛒 Purchased Items
          </button>
          <button
            type="button"
            onClick={() => setItemType("sale")}
            className={`rounded-xl px-4 py-2 text-xs font-semibold transition ${
              itemType === "sale" ? themeSecondaryActive : "text-stone-600 hover:text-stone-900"
            }`}
          >
            💰 Sold Items
          </button>
        </div>

        {/* Switch to Unmapped vs Mapped vs Ignored */}
        <div className="flex rounded-2xl bg-stone-100 p-1">
          <button
            type="button"
            onClick={() => setViewMode("unmapped")}
            className={`rounded-xl px-4 py-2 text-xs font-semibold transition ${
              viewMode === "unmapped" ? themeSecondaryActive : "text-stone-600 hover:text-stone-900"
            }`}
          >
            Unmapped Items
          </button>
          <button
            type="button"
            onClick={() => setViewMode("mapped")}
            className={`rounded-xl px-4 py-2 text-xs font-semibold transition ${
              viewMode === "mapped" ? themeSecondaryActive : "text-stone-600 hover:text-stone-900"
            }`}
          >
            Mapped Items
          </button>
          <button
            type="button"
            onClick={() => setViewMode("ignored")}
            className={`rounded-xl px-4 py-2 text-xs font-semibold transition ${
              viewMode === "ignored" ? themeSecondaryActive : "text-stone-600 hover:text-stone-900"
            }`}
          >
            Ignored Items
          </button>
        </div>
      </div>

      {itemType === "purchase" ? (
        /* ══ PURCHASE INVOICE ITEMS MONITOR ══ */
        <section className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-base font-bold text-stone-950">
              Purchased Items ({filteredPurchaseItems.length})
            </h2>
            <div className="w-full max-w-md">
              <input
                type="text"
                placeholder="Search purchased items by document, name, or counterparty..."
                value={purchaseSearchQuery}
                onChange={(e) => setPurchaseSearchQuery(e.target.value)}
                className="w-full rounded-xl border border-stone-200 px-3.5 py-1.5 text-xs outline-none focus:border-stone-400 bg-white text-stone-700"
              />
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-stone-100 max-h-[500px]">
            <table className="w-full min-w-[900px] text-xs text-left">
              <thead className="bg-stone-50 text-stone-700 sticky top-0 z-10 border-b border-stone-200">
                <tr>
                  <th className="px-4 py-3">Document Details</th>
                  <th className="px-4 py-3">Counterparty</th>
                  <th className="px-4 py-3">Invoice Line Item</th>
                  <th className="px-4 py-3">Qty / Price</th>
                  <th className="px-4 py-3 min-w-[300px]">Product Master Mapping</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="py-12 text-center text-stone-400">
                      <div className="flex items-center justify-center gap-2">
                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-stone-300 border-t-stone-600" />
                        Loading...
                      </div>
                    </td>
                  </tr>
                ) : filteredPurchaseItems.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="py-12 text-center text-stone-400">
                      No items found. All invoices caught up!
                    </td>
                  </tr>
                ) : (
                  filteredPurchaseItems.map((item) => {
                    const query = productSearchQueries[item._id] || "";
                    const results = allProducts.filter(p => {
                      const q = query.trim().toLowerCase();
                      if (!q) return true;
                      return (
                        (p.productName || "").toLowerCase().includes(q) ||
                        (p.itemCode || "").toLowerCase().includes(q) ||
                        (p.brand || "").toLowerCase().includes(q) ||
                        (p.category || "").toLowerCase().includes(q)
                      );
                    });
                    const isSaving = actionSaving[item._id] || false;
                    const selectedId = selectedProductIds[item._id] || "";
                    const isOpen = searchDropdownOpen[item._id] || false;

                    return (
                      <tr key={item._id} className="hover:bg-stone-50/50 align-middle">
                        <td className="px-4 py-3">
                          <div className="font-semibold text-stone-900">
                            {item.documentNumber || "-"}
                          </div>
                          <div className="text-[10px] text-stone-500 mt-0.5">
                            {item.transactionDate ? new Date(item.transactionDate).toLocaleDateString("en-IN") : "-"}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-stone-800 font-medium">{item.counterpartyName || "-"}</div>
                          {item.counterpartyGst && (
                            <div className="text-[9px] text-stone-500 mt-0.5">GST: {item.counterpartyGst}</div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-stone-900 font-semibold">{item.itemName || "-"}</div>
                          {item.description && item.description !== item.itemName && (
                            <div className="text-[10px] text-stone-500 mt-1 max-w-[250px] truncate" title={item.description}>
                              {item.description}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-stone-900">
                            {item.quantity} {item.unit || "No"}
                          </div>
                          <div className="text-[10px] text-stone-500 mt-0.5">
                            {Number(item.unitPrice || 0).toLocaleString("en-IN", { style: "currency", currency: "INR" })}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {viewMode === "mapped" ? (
                            <div className="flex flex-col gap-1.5">
                              <div className="rounded-xl bg-green-50 border border-green-200 p-2.5 max-w-[360px]">
                                <div className="flex items-center justify-between">
                                  <span className="font-mono text-[9px] font-bold text-green-700 bg-green-100 px-1.5 py-0.5 rounded">
                                    {item.itemCode || "Mapped"}
                                  </span>
                                  {item.mappedAt && (
                                    <span className="text-[9px] text-stone-500">
                                      Mapped: {new Date(item.mappedAt).toLocaleDateString()}
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs font-semibold text-stone-800 mt-1.5 truncate">
                                  {item.itemName}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleUnmapItem(item._id)}
                                disabled={isSaving}
                                className="w-fit text-[10px] font-bold text-red-600 hover:text-red-700 flex items-center gap-1 mt-0.5 disabled:opacity-50"
                              >
                                {isSaving ? "Reversing..." : "✕ Unmap Item"}
                              </button>
                            </div>
                          ) : viewMode === "ignored" ? (
                            <div className="flex flex-col gap-1.5 py-1 max-w-[400px]">
                              <div className="rounded-xl bg-red-50 border border-red-200 p-2.5 max-w-[360px]">
                                <span className="font-mono text-[9px] font-bold text-red-700 bg-red-100 px-1.5 py-0.5 rounded">
                                  Ignored
                                </span>
                                <div className="text-xs font-semibold text-stone-800 mt-1.5 truncate">
                                  {item.itemName}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleUnignoreItem(item._id)}
                                disabled={isSaving}
                                className="w-fit text-[10px] font-bold text-green-600 hover:text-green-700 flex items-center gap-1 mt-0.5 disabled:opacity-50"
                              >
                                {isSaving ? "Restoring..." : "↺ Restore to Unmapped"}
                              </button>
                            </div>
                          ) : (
                            <div className="flex flex-col gap-1.5 py-1 max-w-[400px]">
                              <div className="relative">
                                <input
                                  type="text"
                                  placeholder="🔍 Search Material Stock..."
                                  value={query}
                                  onFocus={() => setSearchDropdownOpen(prev => ({ ...prev, [item._id]: true }))}
                                  onChange={(e) => searchProductMaster(item._id, e.target.value)}
                                  className="w-full rounded-xl border border-stone-200 px-3 py-1.5 text-xs outline-none focus:border-stone-400 bg-white text-stone-700"
                                />

                                {isOpen && (
                                  <div className="absolute left-0 right-0 mt-1 max-h-56 overflow-y-auto rounded-xl border border-stone-200 bg-white shadow-xl z-20">
                                    <div className="flex items-center justify-between border-b border-stone-100 bg-stone-50 px-3 py-1 text-[9px] font-bold text-stone-400 sticky top-0">
                                      <span>Select matching item</span>
                                      <button 
                                        type="button"
                                        onClick={() => setSearchDropdownOpen(prev => ({ ...prev, [item._id]: false }))} 
                                        className="text-stone-500 hover:text-stone-900"
                                      >
                                        ✕
                                      </button>
                                    </div>
                                    {results.length === 0 ? (
                                      <div className="p-3 text-[11px] text-stone-400 italic">No matches found.</div>
                                    ) : (
                                      results.map((p) => (
                                        <button
                                          key={p.productId || p._id}
                                          type="button"
                                          onClick={() => {
                                            setSelectedProductIds(prev => ({ ...prev, [item._id]: p.productId }));
                                            setProductSearchQueries(prev => ({ 
                                              ...prev, 
                                              [item._id]: `${p.itemCode} - ${p.productName}` 
                                            }));
                                            setSearchDropdownOpen(prev => ({ ...prev, [item._id]: false }));
                                          }}
                                          className="w-full text-left px-3 py-1.5 hover:bg-stone-50 border-b border-stone-50 last:border-0"
                                        >
                                          <div className="flex items-center justify-between">
                                            <span className="font-mono text-[9px] font-semibold text-stone-600 bg-stone-100 px-1 rounded">
                                              {p.itemCode}
                                            </span>
                                            <span className="text-[8px] font-bold text-stone-500">
                                              {p.category} / {p.brand}
                                            </span>
                                          </div>
                                          <div className="text-[11px] text-stone-800 mt-0.5 truncate">
                                            {p.productName} {p.voltage ? `(${p.voltage})` : ""}
                                          </div>
                                        </button>
                                      ))
                                    )}
                                  </div>
                                )}
                              </div>

                              <div className="flex flex-wrap items-center gap-1.5">
                                <button
                                  type="button"
                                  onClick={() => handleMapItem(item._id)}
                                  disabled={!selectedId || isSaving}
                                  className={`rounded-xl px-3 py-1.5 text-[10px] font-semibold transition ${
                                    selectedId && !isSaving 
                                      ? themeAccent 
                                      : "bg-stone-100 text-stone-400 cursor-not-allowed"
                                  }`}
                                >
                                  {isSaving ? "Mapping..." : "Map to Selection"}
                                </button>
                                <span className="text-[10px] text-stone-400">or</span>
                                <button
                                  type="button"
                                  onClick={() => openCreateModal(item)}
                                  disabled={isSaving}
                                  className="rounded-xl border border-stone-200 hover:border-stone-400 bg-white px-2.5 py-1.5 text-[10px] font-bold text-stone-700 transition disabled:opacity-50"
                                >
                                  ＋ Create & Map
                                </button>
                                <span className="text-[10px] text-stone-400">or</span>
                                <button
                                  type="button"
                                  onClick={() => handleIgnoreItem(item._id)}
                                  disabled={isSaving}
                                  className="rounded-xl border border-red-200 hover:border-red-400 hover:bg-red-50 bg-white px-2.5 py-1.5 text-[10px] font-bold text-red-600 transition disabled:opacity-50"
                                >
                                  Ignore
                                </button>
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </section>
      ) : (
        /* ══ SALES INVOICE ITEMS MONITOR ══ */
        <section className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-base font-bold text-stone-950">
              Sold Items ({filteredSalesItems.length})
            </h2>
            <div className="w-full max-w-md">
              <input
                type="text"
                placeholder="Search sold items by document, name, or counterparty..."
                value={salesSearchQuery}
                onChange={(e) => setSalesSearchQuery(e.target.value)}
                className="w-full rounded-xl border border-stone-200 px-3.5 py-1.5 text-xs outline-none focus:border-stone-400 bg-white text-stone-700"
              />
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-stone-100 max-h-[500px]">
            <table className="w-full min-w-[900px] text-xs text-left">
              <thead className="bg-stone-50 text-stone-700 sticky top-0 z-10 border-b border-stone-200">
                <tr>
                  <th className="px-4 py-3">Document Details</th>
                  <th className="px-4 py-3">Counterparty</th>
                  <th className="px-4 py-3">Invoice Line Item</th>
                  <th className="px-4 py-3">Qty / Price</th>
                  <th className="px-4 py-3 min-w-[300px]">Product Master Mapping</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="py-12 text-center text-stone-400">
                      <div className="flex items-center justify-center gap-2">
                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-stone-300 border-t-stone-600" />
                        Loading...
                      </div>
                    </td>
                  </tr>
                ) : filteredSalesItems.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="py-12 text-center text-stone-400">
                      No items found. All invoices caught up!
                    </td>
                  </tr>
                ) : (
                  filteredSalesItems.map((item) => {
                    const query = productSearchQueries[item._id] || "";
                    const results = allProducts.filter(p => {
                      const q = query.trim().toLowerCase();
                      if (!q) return true;
                      return (
                        (p.productName || "").toLowerCase().includes(q) ||
                        (p.itemCode || "").toLowerCase().includes(q) ||
                        (p.brand || "").toLowerCase().includes(q) ||
                        (p.category || "").toLowerCase().includes(q)
                      );
                    });
                    const isSaving = actionSaving[item._id] || false;
                    const selectedId = selectedProductIds[item._id] || "";
                    const isOpen = searchDropdownOpen[item._id] || false;

                    return (
                      <tr key={item._id} className="hover:bg-stone-50/50 align-middle">
                        <td className="px-4 py-3">
                          <div className="font-semibold text-stone-900">
                            {item.documentNumber || "-"}
                          </div>
                          <div className="text-[10px] text-stone-500 mt-0.5">
                            {item.transactionDate ? new Date(item.transactionDate).toLocaleDateString("en-IN") : "-"}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-stone-800 font-medium">{item.counterpartyName || "-"}</div>
                          {item.counterpartyGst && (
                            <div className="text-[9px] text-stone-500 mt-0.5">GST: {item.counterpartyGst}</div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-stone-900 font-semibold">{item.itemName || "-"}</div>
                          {item.description && item.description !== item.itemName && (
                            <div className="text-[10px] text-stone-500 mt-1 max-w-[250px] truncate" title={item.description}>
                              {item.description}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-stone-900">
                            {item.quantity} {item.unit || "No"}
                          </div>
                          <div className="text-[10px] text-stone-500 mt-0.5">
                            {Number(item.unitPrice || 0).toLocaleString("en-IN", { style: "currency", currency: "INR" })}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {viewMode === "mapped" ? (
                            <div className="flex flex-col gap-1.5">
                              <div className="rounded-xl bg-green-50 border border-green-200 p-2.5 max-w-[360px]">
                                <div className="flex items-center justify-between">
                                  <span className="font-mono text-[9px] font-bold text-green-700 bg-green-100 px-1.5 py-0.5 rounded">
                                    {item.itemCode || "Mapped"}
                                  </span>
                                  {item.mappedAt && (
                                    <span className="text-[9px] text-stone-500">
                                      Mapped: {new Date(item.mappedAt).toLocaleDateString()}
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs font-semibold text-stone-800 mt-1.5 truncate">
                                  {item.itemName}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleUnmapItem(item._id)}
                                disabled={isSaving}
                                className="w-fit text-[10px] font-bold text-red-600 hover:text-red-700 flex items-center gap-1 mt-0.5 disabled:opacity-50"
                              >
                                {isSaving ? "Reversing..." : "✕ Unmap Item"}
                              </button>
                            </div>
                          ) : viewMode === "ignored" ? (
                            <div className="flex flex-col gap-1.5 py-1 max-w-[400px]">
                              <div className="rounded-xl bg-red-50 border border-red-200 p-2.5 max-w-[360px]">
                                <span className="font-mono text-[9px] font-bold text-red-700 bg-red-100 px-1.5 py-0.5 rounded">
                                  Ignored
                                </span>
                                <div className="text-xs font-semibold text-stone-800 mt-1.5 truncate">
                                  {item.itemName}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleUnignoreItem(item._id)}
                                disabled={isSaving}
                                className="w-fit text-[10px] font-bold text-green-600 hover:text-green-700 flex items-center gap-1 mt-0.5 disabled:opacity-50"
                              >
                                {isSaving ? "Restoring..." : "↺ Restore to Unmapped"}
                              </button>
                            </div>
                          ) : (
                            <div className="flex flex-col gap-1.5 py-1 max-w-[400px]">
                              <div className="relative">
                                <input
                                  type="text"
                                  placeholder="🔍 Search Material Stock..."
                                  value={query}
                                  onFocus={() => setSearchDropdownOpen(prev => ({ ...prev, [item._id]: true }))}
                                  onChange={(e) => searchProductMaster(item._id, e.target.value)}
                                  className="w-full rounded-xl border border-stone-200 px-3 py-1.5 text-xs outline-none focus:border-stone-400 bg-white text-stone-700"
                                />

                                {isOpen && (
                                  <div className="absolute left-0 right-0 mt-1 max-h-56 overflow-y-auto rounded-xl border border-stone-200 bg-white shadow-xl z-20">
                                    <div className="flex items-center justify-between border-b border-stone-100 bg-stone-50 px-3 py-1 text-[9px] font-bold text-stone-400 sticky top-0">
                                      <span>Select matching item</span>
                                      <button 
                                        type="button"
                                        onClick={() => setSearchDropdownOpen(prev => ({ ...prev, [item._id]: false }))} 
                                        className="text-stone-500 hover:text-stone-900"
                                      >
                                        ✕
                                      </button>
                                    </div>
                                    {results.length === 0 ? (
                                      <div className="p-3 text-[11px] text-stone-400 italic">No matches found.</div>
                                    ) : (
                                      results.map((p) => (
                                        <button
                                          key={p.productId || p._id}
                                          type="button"
                                          onClick={() => {
                                            setSelectedProductIds(prev => ({ ...prev, [item._id]: p.productId }));
                                            setProductSearchQueries(prev => ({ 
                                              ...prev, 
                                              [item._id]: `${p.itemCode} - ${p.productName}` 
                                            }));
                                            setSearchDropdownOpen(prev => ({ ...prev, [item._id]: false }));
                                          }}
                                          className="w-full text-left px-3 py-1.5 hover:bg-stone-50 border-b border-stone-50 last:border-0"
                                        >
                                          <div className="flex items-center justify-between">
                                            <span className="font-mono text-[9px] font-semibold text-stone-600 bg-stone-100 px-1 rounded">
                                              {p.itemCode}
                                            </span>
                                            <span className="text-[8px] font-bold text-stone-500">
                                              {p.category} / {p.brand}
                                            </span>
                                          </div>
                                          <div className="text-[11px] text-stone-800 mt-0.5 truncate">
                                            {p.productName} {p.voltage ? `(${p.voltage})` : ""}
                                          </div>
                                        </button>
                                      ))
                                    )}
                                  </div>
                                )}
                              </div>

                              <div className="flex flex-wrap items-center gap-1.5">
                                <button
                                  type="button"
                                  onClick={() => handleMapItem(item._id)}
                                  disabled={!selectedId || isSaving}
                                  className={`rounded-xl px-3 py-1.5 text-[10px] font-semibold transition ${
                                    selectedId && !isSaving 
                                      ? themeAccent 
                                      : "bg-stone-100 text-stone-400 cursor-not-allowed"
                                  }`}
                                >
                                  {isSaving ? "Mapping..." : "Map to Selection"}
                                </button>
                                <span className="text-[10px] text-stone-400">or</span>
                                <button
                                  type="button"
                                  onClick={() => openCreateModal(item)}
                                  disabled={isSaving}
                                  className="rounded-xl border border-stone-200 hover:border-stone-400 bg-white px-2.5 py-1.5 text-[10px] font-bold text-stone-700 transition disabled:opacity-50"
                                >
                                  ＋ Create & Map
                                </button>
                                <span className="text-[10px] text-stone-400">or</span>
                                <button
                                  type="button"
                                  onClick={() => handleIgnoreItem(item._id)}
                                  disabled={isSaving}
                                  className="rounded-xl border border-red-200 hover:border-red-400 hover:bg-red-50 bg-white px-2.5 py-1.5 text-[10px] font-bold text-red-600 transition disabled:opacity-50"
                                >
                                  Ignore
                                </button>
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Creation and Mapping Modal */}
      {showCreateModal && currentItemToMap && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4 backdrop-blur-[1px]">
          <div className="w-full max-w-2xl rounded-[2rem] bg-white p-6 shadow-2xl animate-fade-in max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-stone-100 pb-4 mb-4">
              <div>
                <h3 className="text-lg font-bold text-stone-950">Create & Map Material Stock Entry</h3>
                <p className="text-xs text-stone-500 mt-0.5">
                  Invoice item: <span className="font-semibold text-stone-800">{currentItemToMap.itemName}</span>
                </p>
              </div>
              <button 
                type="button" 
                onClick={() => setShowCreateModal(false)}
                className="rounded-full bg-stone-100 hover:bg-stone-200 p-2 text-stone-500 hover:text-stone-800 transition"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateAndMap} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-bold text-stone-500">Product Name *</label>
                  <input
                    type="text"
                    required
                    value={newProductData.productName}
                    onChange={(e) => setNewProductData(s => ({ ...s, productName: e.target.value }))}
                    className="w-full rounded-xl border border-stone-200 px-3.5 py-2.5 text-sm outline-none focus:border-stone-400"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-bold text-stone-500">Brand (Product Family) *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. ABB, L&T, Siemens"
                    value={newProductData.brand}
                    onChange={(e) => setNewProductData(s => ({ ...s, brand: e.target.value }))}
                    className="w-full rounded-xl border border-stone-200 px-3.5 py-2.5 text-sm outline-none focus:border-stone-400"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-bold text-stone-500">Type (Category) *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Spare, Trip Coil, MCCB, Relay"
                    value={newProductData.type}
                    onChange={(e) => setNewProductData(s => ({ ...s, type: e.target.value }))}
                    className="w-full rounded-xl border border-stone-200 px-3.5 py-2.5 text-sm outline-none focus:border-stone-400"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-bold text-stone-500">Model (Size)</label>
                  <input
                    type="text"
                    placeholder="e.g. E2.2, E1B, standard"
                    value={newProductData.model}
                    onChange={(e) => setNewProductData(s => ({ ...s, model: e.target.value }))}
                    className="w-full rounded-xl border border-stone-200 px-3.5 py-2.5 text-sm outline-none focus:border-stone-400"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-bold text-stone-500">Voltage (Version)</label>
                  <input
                    type="text"
                    placeholder="e.g. 24V DC, 220V AC, F"
                    value={newProductData.voltage}
                    onChange={(e) => setNewProductData(s => ({ ...s, voltage: e.target.value }))}
                    className="w-full rounded-xl border border-stone-200 px-3.5 py-2.5 text-sm outline-none focus:border-stone-400"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-bold text-stone-500">Pole</label>
                  <input
                    type="text"
                    placeholder="e.g. 3P, 4P"
                    value={newProductData.pole}
                    onChange={(e) => setNewProductData(s => ({ ...s, pole: e.target.value }))}
                    className="w-full rounded-xl border border-stone-200 px-3.5 py-2.5 text-sm outline-none focus:border-stone-400"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-bold text-stone-500">HSN Code</label>
                  <input
                    type="text"
                    placeholder="85362000"
                    value={newProductData.hsnCode}
                    onChange={(e) => setNewProductData(s => ({ ...s, hsnCode: e.target.value }))}
                    className="w-full rounded-xl border border-stone-200 px-3.5 py-2.5 text-sm outline-none focus:border-stone-400"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-bold text-stone-500">List Price (INR)</label>
                  <input
                    type="number"
                    min="0"
                    value={newProductData.listPrice}
                    onChange={(e) => setNewProductData(s => ({ ...s, listPrice: e.target.value }))}
                    className="w-full rounded-xl border border-stone-200 px-3.5 py-2.5 text-sm outline-none focus:border-stone-400"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-bold text-stone-500">Unit</label>
                  <input
                    type="text"
                    placeholder="No, Pcs, Set"
                    value={newProductData.unit}
                    onChange={(e) => setNewProductData(s => ({ ...s, unit: e.target.value }))}
                    className="w-full rounded-xl border border-stone-200 px-3.5 py-2.5 text-sm outline-none focus:border-stone-400"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-bold text-stone-500">Item Code (Optional)</label>
                  <input
                    type="text"
                    placeholder="Auto-generated if left blank"
                    value={newProductData.itemCode}
                    onChange={(e) => setNewProductData(s => ({ ...s, itemCode: e.target.value }))}
                    className="w-full rounded-xl border border-stone-200 px-3.5 py-2.5 text-sm outline-none focus:border-stone-400"
                  />
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold text-stone-500">Description</label>
                <textarea
                  value={newProductData.description}
                  onChange={(e) => setNewProductData(s => ({ ...s, description: e.target.value }))}
                  rows={2}
                  className="w-full rounded-xl border border-stone-200 px-3.5 py-2.5 text-sm outline-none focus:border-stone-400"
                />
              </div>

              <div className="flex justify-end gap-3 border-t border-stone-100 pt-4 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="rounded-xl border border-stone-200 px-5 py-2.5 text-sm font-semibold hover:bg-stone-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`rounded-xl px-6 py-2.5 text-sm font-semibold transition ${themeAccent}`}
                >
                  Create & Map
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
